import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Home } from '../features/Home';
import { Settings } from '../pages/Settings';
import { GitLabFileBrowser } from '../features/GitLabFileBrowser';
import { Config } from '../../types/config';
import { ConfigService } from '../../services/configService';
import { GitLabFile } from '../../services/gitlabService';
import GitLabService from '../../services/gitlabService';
import { EnhancedProxyService as ProxyService } from '../../services/enhancedProxyService';

export const AppLayout: React.FC = () => {
  const { isOpen: isSidebarOpen, onToggle: toggleSidebar } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isGitlabBrowserOpen, onToggle: toggleGitlabBrowser } = useDisclosure({ defaultIsOpen: false });
  const location = useLocation();
  
  const handleToggleGitlabBrowser = async () => {
    // Check if we have a GitLab URL (either from config or default)
    try {
      const resolvedUrl = await GitLabService.resolveGitLabUrl(selectedConfig?.gitlabUrl);
      if (!resolvedUrl) {
        toast({
          title: 'GitLab Repository Not Configured',
          description: 'Please configure a default GitLab repository or document-specific repository in Settings.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setResolvedGitlabUrl(resolvedUrl);
      toggleGitlabBrowser();
    } catch (error) {
      toast({
        title: 'GitLab Configuration Error',
        description: 'Failed to check GitLab repository configuration.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRepositoryChange = (newUrl: string) => {
    setResolvedGitlabUrl(newUrl);
    toast({
      title: 'Repository Changed',
      description: `Now browsing: ${newUrl.replace(/^https?:\/\//, '')}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };
  const [selectedConfig, setSelectedConfig] = useState<Config | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [gitlabBrowserWidth, setGitlabBrowserWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [isGitlabResizing, setIsGitlabResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const gitlabBrowserRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [gitlabUploadCallback, setGitlabUploadCallback] = useState<((variable: string, gitlabUrl: string, filePath: string) => Promise<void>) | null>(null);
  const [wasOnSettings, setWasOnSettings] = useState(false);
  const [resolvedGitlabUrl, setResolvedGitlabUrl] = useState<string | null>(null);
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const resizeHandleColor = useColorModeValue('gray.300', 'gray.600');

  const minSidebarWidth = 200;
  const maxSidebarWidth = Math.floor(window.innerWidth * 0.4);
  const collapsedWidth = 60;
  const minGitlabBrowserWidth = 250;
  const maxGitlabBrowserWidth = Math.floor(window.innerWidth * 0.4);

  const currentSidebarWidth = isSidebarOpen ? sidebarWidth : collapsedWidth;
  const currentGitlabBrowserWidth = isGitlabBrowserOpen ? gitlabBrowserWidth : 0;

  // Handle GitLab file upload
  const handleGitLabFileUpload = async (file: GitLabFile) => {
    if (!selectedConfig) {
      toast({
        title: 'No Configuration Selected',
        description: 'Please select a configuration before uploading files.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Resolve GitLab URL (config-specific or default)
    const resolvedGitlabUrl = await GitLabService.resolveGitLabUrl(selectedConfig.gitlabUrl);
    if (!resolvedGitlabUrl) {
      toast({
        title: 'GitLab Repository Not Available',
        description: 'No GitLab repository configured. Please configure a default repository in Settings.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!gitlabUploadCallback) {
      toast({
        title: 'Upload Handler Not Ready',
        description: 'Please wait for the form to load before uploading files.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Let InputArea handle the parameter association through currentFileParameter state
      await gitlabUploadCallback('', resolvedGitlabUrl, file.path);
    } catch (error: any) {
      console.error('GitLab file upload error:', error);
      // Error handling is done in the InputArea component
    }
  };

  // Handle GitLab upload callback registration
  const handleGitLabUploadRequest = (callback: (variable: string, gitlabUrl: string, filePath: string) => Promise<void>) => {
    setGitlabUploadCallback(() => callback);
  };

  // Load configs and set the active one on mount
  const loadActiveConfig = async () => {
    try {
      console.log('🔄 AppLayout: Reloading configuration...');
      const configs = await ConfigService.getAllConfigs();
      if (configs && configs.length > 0) {
        const active = configs.find(c => c.isActive);
        const newSelectedConfig = active || configs[0];
        console.log('🔄 AppLayout: Loaded config with GitLab URL:', newSelectedConfig.gitlabUrl);
        setSelectedConfig(newSelectedConfig);
      } else {
        setSelectedConfig(null);
      }
    } catch (err) {
      console.error('❌ AppLayout: Failed to reload configuration:', err);
      setSelectedConfig(null);
    }
  };

  useEffect(() => {
    loadActiveConfig();
  }, []);

  // Track when we're on settings page and reload config when we leave
  useEffect(() => {
    if (location.pathname === '/settings') {
      setWasOnSettings(true);
    } else if (wasOnSettings) {
      // We just left the settings page, reload the config
      console.log('🔄 AppLayout: Returning from Settings, reloading config...');
      loadActiveConfig();
      setWasOnSettings(false);
    }
  }, [location.pathname, wasOnSettings]);

  const handleConfigSelect = (config: Config) => {
    setSelectedConfig(config);
  };

  const startResize = (e: React.MouseEvent) => {
    if (!isSidebarOpen) return;
    
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth + (e.clientX - startX);
      setSidebarWidth(Math.max(minSidebarWidth, Math.min(newWidth, maxSidebarWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handleWindowResize = () => {
      const newMaxWidth = Math.floor(window.innerWidth * 0.4);
      if (sidebarWidth > newMaxWidth) {
        setSidebarWidth(newMaxWidth);
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [sidebarWidth]);

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Fixed Header */}
      <Header />
      
      {/* Main layout */}
      <Flex pt="80px" h="100vh">
        <Routes>
          <Route 
            path="/" 
            element={
              <>
                {/* Resizable Sidebar */}
                <Box
                  ref={sidebarRef}
                  w={`${currentSidebarWidth}px`}
                  bg={sidebarBg}
                  borderRight="1px"
                  borderColor={borderColor}
                  overflow="hidden"
                  transition={isResizing ? 'none' : 'width 0.2s'}
                  flexShrink={0}
                  h="calc(100vh - 80px)"
                  position="relative"
                >
                  <Sidebar 
                    isCollapsed={!isSidebarOpen}
                    selectedConfig={selectedConfig}
                    onConfigSelect={handleConfigSelect}
                    onToggleCollapse={toggleSidebar}
                  />
                  {/* Resize Handle */}
                  {isSidebarOpen && (
                    <Box
                      position="absolute"
                      top="0"
                      right="0"
                      w="4px"
                      h="100%"
                      cursor="ew-resize"
                      zIndex={10}
                      bg={resizeHandleColor}
                      _hover={{
                        bg: 'blue.400',
                        boxShadow: '0 0 4px rgba(66, 153, 225, 0.5)',
                      }}
                      onMouseDown={startResize}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box
                        w="1px"
                        h="100%"
                        bg={isResizing ? 'blue.400' : resizeHandleColor}
                        boxShadow={isResizing ? '0 0 4px rgba(66, 153, 225, 0.5)' : 'none'}
                      />
                    </Box>
                  )}
                </Box>
                {/* Main Content Area */}
                <Flex
                  direction="row"
                  flex="1"
                  h="calc(100vh - 80px)"
                  overflow="hidden"
                >
                  <Box flex="1" overflow="hidden">
                    <Home 
                      selectedConfig={selectedConfig} 
                      onGitLabFileUploadRequest={handleGitLabUploadRequest}
                      onToggleGitlabBrowser={handleToggleGitlabBrowser}
                    />
                  </Box>
                  {/* GitLab File Browser */}
                  {isGitlabBrowserOpen && (
                    <Box
                      w={`${currentGitlabBrowserWidth}px`}
                      bg={sidebarBg}
                      borderLeft="1px"
                      borderColor={borderColor}
                      overflow="hidden"
                      flexShrink={0}
                      h="100%"
                    >
                      <GitLabFileBrowser 
                        gitlabUrl={resolvedGitlabUrl || undefined} 
                        configGitlabUrl={selectedConfig?.gitlabUrl}
                        onFileUpload={handleGitLabFileUpload}
                        onClose={toggleGitlabBrowser}
                        onRepositoryChange={handleRepositoryChange}
                      />
                    </Box>
                  )}
                </Flex>
              </>
            }
          />
          <Route 
            path="/settings" 
            element={
              <Flex direction="column" flex="1" h="calc(100vh - 80px)" overflow="hidden">
                <Box flex="1" overflow="auto">
                  <Settings />
                </Box>
              </Flex>
            }
          />
        </Routes>
      </Flex>
    </Box>
  );
};